/**
 * apiError.js — Single source of truth for turning a decrypted API error payload
 * into a human-readable message (§5/§9 DRY, §4 back↔front contract).
 *
 * The backend is inconsistent about the field it uses for error reasons: some
 * controllers return `{ message }` (e.g. bugReport), others return `{ error }`
 * (e.g. featureRequest). axiosWithToken used to read only `message`, so every
 * `error`-keyed failure (rate limit, duplicate title, invalid category, ...)
 * collapsed to the opaque "Server error" — the user never saw the real reason.
 *
 * Reading both fields here keeps every call site honest regardless of which key
 * the controller picked.
 */
export function extractApiErrorMessage(data, fallback = 'Server error') {
  if (data && typeof data === 'object') {
    return data.message || data.error || fallback;
  }
  return fallback;
}

/**
 * ApiNotFoundError — erreur typée levée par le client HTTP central sur tout 404
 * (vague 4 lot 3 : la NAVIGATION sort du transport).
 *
 * Historique : apiCallWithToken faisait un `window.location.href = '/'` en dur sur
 * tout 404 non whitelisté — deux bugs documentés en ont découlé (fiches asset/nft
 * introuvables qui rebondissaient sur la home au lieu d'afficher NotFoundBlock,
 * claim-collectible sans MonthAsset qui hard-redirigeait au lieu d'un toast) et
 * chaque page touchée devait rustiner le transport avec une exception d'URL.
 *
 * Désormais : le transport THROW toujours cette erreur typée. La décision de
 * rediriger vit dans une politique nommée du client (apply404RedirectPolicy),
 * surchargeable par appel via l'option `{ no404Redirect: true }` — plus aucune
 * whitelist d'URL dans le transport.
 *
 * Compat : message par défaut 'NOT_FOUND' + `status = 404`, mêmes champs que les
 * anciennes erreurs rustinées (les catch existants sur `err.status === 404` ou
 * `err.message === 'NOT_FOUND'` restent valides). `url` porte l'URL introuvable.
 */
export class ApiNotFoundError extends Error {
  constructor(url, message = 'NOT_FOUND') {
    super(message);
    this.name = 'ApiNotFoundError';
    this.status = 404;
    this.url = url;
  }
}
